import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Coins, QrCode, Plus, Trash, CheckCircle, XCircle, Copy, Pause, Play, EnvelopeSimple, UserCirclePlus, PencilSimple } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import AffiliateDetailDialog from '@/components/AffiliateDetailDialog';
import { toast } from 'sonner';

const cents = (c) => `€${((c || 0) / 100).toFixed(2)}`;

export default function AdminAffiliatePanel({ token, apiBase }) {
  const API = apiBase;
  const auth = { Authorization: `Bearer ${token}` };

  const [affiliates, setAffiliates] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ code: '', name: '', email: '', cpaTicketCents: 2000, cpaRegistrationCents: 0, cpcCents: 0, revsharePercent: 0 });
  const [filter, setFilter] = useState('pending'); // pending | approved | rejected | paid | all
  const [detailCode, setDetailCode] = useState(null);
  const [editTarget, setEditTarget] = useState(null); // affiliate object being edited

  const loadAffiliates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/affiliates`, { headers: auth });
      const data = await safeReadJson(res);
      if (res.ok) setAffiliates(data.affiliates || []);
    } catch (e) { /* silent */ }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API, token]);

  const loadRewards = useCallback(async () => {
    try {
      const q = filter && filter !== 'all' ? `?status_filter=${filter}` : '';
      const res = await fetch(`${API}/admin/affiliate-rewards${q}`, { headers: auth });
      const data = await safeReadJson(res);
      if (res.ok) setRewards(data.rewards || []);
    } catch (e) { /* silent */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API, token, filter]);

  useEffect(() => { loadAffiliates(); }, [loadAffiliates]);
  useEffect(() => { loadRewards(); }, [loadRewards]);

  // Robust JSON reader: never calls .json() (which interacts badly with Sentry breadcrumbs).
  // Reads body as text exactly once and parses; falls back to empty object on parse failure.
  const safeReadJson = async (res) => {
    try {
      // Clone before reading: Sentry's browser fetch instrumentation drains the
      // original response stream for breadcrumbs, leaving res.text() empty.
      const text = await res.clone().text();
      if (!text) return {};
      try { return JSON.parse(text); } catch { return { _raw: text }; }
    } catch {
      return {};
    }
  };

  const createAffiliate = async () => {
    const cleanCode = form.code.trim().toUpperCase().replace(/[^A-Z0-9_-]/g, '');
    if (!cleanCode) { toast.error('Code is required'); return; }
    if (cleanCode.length < 3) { toast.error('Code must be at least 3 characters (A-Z, 0-9, _ -)'); return; }
    // Anti-fraud caps enforced by backend (Pydantic). Catch them client-side for a clear message.
    if (form.cpcCents > 500) {
      toast.error('CPC per click is capped at €5.00 (anti-fraud). Lower the value and try again.', { duration: 8000 });
      return;
    }
    if (form.cpaTicketCents > 1_000_000 || form.cpaRegistrationCents > 1_000_000) {
      toast.error('CPA reward must be ≤ €10,000.', { duration: 8000 });
      return;
    }
    if (form.revsharePercent < 0 || form.revsharePercent > 100) {
      toast.error('Revshare must be between 0 and 100%.', { duration: 8000 });
      return;
    }
    // Pydantic EmailStr refuses empty strings — send null instead so the field is optional.
    const payload = {
      ...form,
      code: cleanCode,
      name: form.name.trim() || null,
      email: form.email.trim() || null,
    };
    try {
      const res = await fetch(`${API}/admin/affiliates`, {
        method: 'POST',
        headers: { ...auth, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await safeReadJson(res);
      if (res.ok) {
        const acct = data.partnerAccount;
        if (acct?.action === 'created' || acct?.action === 'reset') {
          toast.success(`Affiliate ${data.affiliate.code} created — partner login emailed to ${acct.to}`);
        } else if (acct?.action === 'skipped') {
          toast.warning(`Affiliate ${data.affiliate.code} created, but partner login could not be set up: ${acct.error}`);
        } else {
          toast.success(`Affiliate ${data.affiliate.code} created`);
        }
        setShowCreate(false);
        setForm({ code: '', name: '', email: '', cpaTicketCents: 2000, cpaRegistrationCents: 0, cpcCents: 0, revsharePercent: 0 });
        loadAffiliates();
      } else {
        // Pydantic validation errors come back as an array under `detail`; flatten to readable text.
        let msg = `Failed to create (HTTP ${res.status})`;
        if (Array.isArray(data.detail) && data.detail.length > 0) {
          msg = data.detail.map((d) => `${d.loc?.slice(-1)[0] || 'field'}: ${d.msg}`).join('; ');
        } else if (typeof data.detail === 'string') {
          msg = data.detail;
        } else if (typeof data._raw === 'string' && data._raw.trim()) {
          msg = data._raw.slice(0, 240);
        }
        toast.error(msg, { duration: 8000 });
      }
    } catch (e) {
      toast.error(`Network error: ${e.message || 'request failed'}`, { duration: 8000 });
    }
  };

  const toggleStatus = async (code, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'paused' : 'active';
    try {
      const res = await fetch(`${API}/admin/affiliates/${code}`, {
        method: 'PATCH',
        headers: { ...auth, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) {
        toast.success(`Affiliate ${code} → ${newStatus}`);
        loadAffiliates();
      } else {
        const d = await safeReadJson(res);
        toast.error(d.detail || 'Failed');
      }
    } catch (e) {
      toast.error('Network error');
    }
  };

  const deleteAffiliate = async (code) => {
    if (!window.confirm(`Delete affiliate ${code}? This refuses if any rewards exist.`)) return;
    try {
      const res = await fetch(`${API}/admin/affiliates/${code}`, { method: 'DELETE', headers: auth });
      const data = await safeReadJson(res);
      if (res.ok) {
        toast.success(`Deleted ${code}`);
        loadAffiliates();
      } else {
        toast.error(data.detail || 'Failed to delete');
      }
    } catch (e) {
      toast.error('Network error');
    }
  };

  const copyLink = async (code) => {
    const link = `${window.location.origin}/?ref=${code}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Referral link copied');
    } catch (e) {
      toast.error('Copy failed');
    }
  };

  const sendWelcome = async (code) => {
    try {
      const res = await fetch(`${API}/admin/affiliates/${code}/send-welcome`, {
        method: 'POST',
        headers: auth,
      });
      const data = await safeReadJson(res);
      if (res.ok) {
        toast.success(`Welcome email sent to ${data.to}`);
      } else {
        toast.error(data.detail || 'Send failed');
      }
    } catch (e) {
      toast.error('Network error');
    }
  };

  const saveEdit = async (updates) => {
    if (!editTarget?.code) return;
    try {
      const res = await fetch(`${API}/admin/affiliates/${editTarget.code}`, {
        method: 'PATCH',
        headers: { ...auth, 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      const data = await safeReadJson(res);
      if (res.ok) {
        toast.success(`${editTarget.code} updated`);
        setEditTarget(null);
        loadAffiliates();
      } else {
        let msg = 'Update failed';
        if (Array.isArray(data.detail) && data.detail.length > 0) {
          msg = data.detail.map((d) => `${d.loc?.slice(-1)[0] || 'field'}: ${d.msg}`).join('; ');
        } else if (typeof data.detail === 'string') {
          msg = data.detail;
        }
        toast.error(msg);
      }
    } catch (e) {
      toast.error(`Network error: ${e.message || 'request failed'}`);
    }
  };


  const rewardAction = async (rewardId, action, txHash = null) => {
    const url = `${API}/admin/affiliate-rewards/${rewardId}/${action}`;
    const body = action === 'mark-paid' ? { txHash } : (action === 'reject' ? { note: 'rejected by admin' } : {});
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { ...auth, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        toast.success(`Reward ${action.replace('-', ' ')}`);
        loadRewards();
        loadAffiliates();
      } else {
        const d = await safeReadJson(res);
        toast.error(d.detail || 'Failed');
      }
    } catch (e) {
      toast.error('Network error');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="admin-affiliate-section"
      className="mb-10"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Coins size={26} weight="duotone" className="text-[#cba135]" />
          <h3 className="heading-font text-lg sm:text-xl font-medium text-[#cba135]">Affiliate Partners</h3>
        </div>
        <Button
          data-testid="affiliate-create-btn"
          onClick={() => setShowCreate(true)}
          size="sm"
          className="bg-[#cba135] hover:bg-[#b8902f] text-[#0e0b04] font-medium"
        >
          <Plus size={16} weight="bold" /> New affiliate
        </Button>
      </div>

      <div className="bg-[#1a1508] border border-[#cba135]/15 rounded-lg p-5">
        {loading ? (
          <p className="text-[#cba135]/60 text-sm">Loading…</p>
        ) : affiliates.length === 0 ? (
          <p className="text-[#cba135]/60 text-sm" data-testid="affiliate-empty">No affiliate partners yet. Create the first one to share a QR code.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[#a8872b] uppercase text-xs tracking-wider">
                <tr className="border-b border-[#cba135]/10">
                  <th className="text-left py-2 px-2">Code</th>
                  <th className="text-left py-2 px-2">Name</th>
                  <th className="text-right py-2 px-2" title="T=ticket, R=registration, C=click, %=revshare">Rewards</th>
                  <th className="text-right py-2 px-2">Clicks</th>
                  <th className="text-right py-2 px-2">Regs</th>
                  <th className="text-right py-2 px-2" title="Registrations / Clicks">CR %</th>
                  <th className="text-right py-2 px-2">Pending</th>
                  <th className="text-right py-2 px-2">Paid</th>
                  <th className="text-right py-2 px-2" title="Paid / Regs">Paid %</th>
                  <th className="text-center py-2 px-2">Status</th>
                  <th className="text-right py-2 px-2">Actions</th>
                </tr>
              </thead>
              <tbody className="text-[#cba135]/90">
                {affiliates.map((a) => {
                  const crPct = a.clicks > 0 ? Math.round((a.registrations / a.clicks) * 1000) / 10 : 0;
                  const paidPct = a.registrations > 0 ? Math.round((a.paidRewards / a.registrations) * 1000) / 10 : 0;
                  return (
                  <tr key={a.code} className="border-b border-[#cba135]/5 hover:bg-[#cba135]/5" data-testid={`affiliate-row-${a.code}`}>
                    <td className="py-3 px-2 font-mono font-semibold">
                      <button
                        onClick={() => setDetailCode(a.code)}
                        className="text-[#cba135] hover:text-[#e6be4b] underline-offset-2 hover:underline transition-colors"
                        title="Open partner live view"
                        data-testid={`affiliate-detail-open-${a.code}`}
                      >
                        {a.code}
                      </button>
                    </td>
                    <td className="py-3 px-2">{a.name || <span className="text-[#cba135]/40">—</span>}</td>
                    <td className="py-3 px-2 text-right text-xs">
                      {(() => {
                        const ticketC = a.cpaTicketCents ?? a.cpaAmountCents ?? 0;
                        const parts = [];
                        if (ticketC > 0) parts.push(`€${(ticketC / 100).toFixed(2)}/T`);
                        if (a.cpaRegistrationCents > 0) parts.push(`€${(a.cpaRegistrationCents / 100).toFixed(2)}/R`);
                        if (a.cpcCents > 0) parts.push(`€${(a.cpcCents / 100).toFixed(2)}/C`);
                        if (a.revsharePercent > 0) parts.push(`${a.revsharePercent}%`);
                        return parts.length ? parts.join(' + ') : <span className="text-[#cba135]/40">—</span>;
                      })()}
                    </td>
                    <td className="py-3 px-2 text-right">{a.clicks}</td>
                    <td className="py-3 px-2 text-right">{a.registrations}</td>
                    <td className="py-3 px-2 text-right">
                      <span className={crPct >= 20 ? 'text-emerald-300' : crPct >= 5 ? 'text-[#cba135]' : 'text-[#cba135]/40'} data-testid={`affiliate-cr-${a.code}`}>
                        {a.clicks > 0 ? `${crPct}%` : '—'}
                      </span>
                    </td>
                    <td className="py-3 px-2 text-right text-amber-400">{a.pendingRewards}</td>
                    <td className="py-3 px-2 text-right text-emerald-400">{a.paidRewards}</td>
                    <td className="py-3 px-2 text-right">
                      <span className={paidPct >= 30 ? 'text-emerald-300' : paidPct >= 10 ? 'text-[#cba135]' : 'text-[#cba135]/40'} data-testid={`affiliate-paid-pct-${a.code}`}>
                        {a.registrations > 0 ? `${paidPct}%` : '—'}
                      </span>
                    </td>
                    <td className="py-3 px-2 text-center">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${a.status === 'active' ? 'bg-emerald-500/10 text-emerald-300' : 'bg-[#cba135]/10 text-[#cba135]/60'}`}>
                        {a.status}
                      </span>
                    </td>
                    <td className="py-3 px-2 text-right">
                      <div className="flex items-center gap-1 justify-end">
                        <button
                          onClick={() => copyLink(a.code)}
                          className="p-1.5 rounded hover:bg-[#cba135]/10 text-[#cba135]/70 hover:text-[#cba135]"
                          title="Copy referral link"
                          data-testid={`affiliate-copy-${a.code}`}
                        >
                          <Copy size={16} />
                        </button>
                        <a
                          href={`${API}/affiliates/${a.code}/qr`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 rounded hover:bg-[#cba135]/10 text-[#cba135]/70 hover:text-[#cba135] inline-flex"
                          title="Open QR code PNG"
                          data-testid={`affiliate-qr-${a.code}`}
                        >
                          <QrCode size={16} />
                        </a>
                        {a.email && (
                          <button
                            onClick={() => sendWelcome(a.code)}
                            className="p-1.5 rounded hover:bg-[#cba135]/10 text-[#cba135]/70 hover:text-[#cba135]"
                            title={`Send welcome email to ${a.email}`}
                            data-testid={`affiliate-mail-${a.code}`}
                          >
                            <EnvelopeSimple size={16} />
                          </button>
                        )}
                        {a.email && (
                          <button
                            onClick={() => createPartnerAccount(a.code, a.email)}
                            className="p-1.5 rounded hover:bg-[#cba135]/10 text-[#cba135]/70 hover:text-[#cba135]"
                            title={`Create / reset login account for ${a.email}`}
                            data-testid={`affiliate-create-account-${a.code}`}
                          >
                            <UserCirclePlus size={16} />
                          </button>
                        )}
                        <button
                          onClick={() => setEditTarget(a)}
                          className="p-1.5 rounded hover:bg-[#cba135]/10 text-[#cba135]/70 hover:text-[#cba135]"
                          title="Edit name / email / CPA"
                          data-testid={`affiliate-edit-${a.code}`}
                        >
                          <PencilSimple size={16} />
                        </button>
                        <button
                          onClick={() => toggleStatus(a.code, a.status)}
                          className="p-1.5 rounded hover:bg-[#cba135]/10 text-[#cba135]/70 hover:text-[#cba135]"
                          title={a.status === 'active' ? 'Pause' : 'Activate'}
                          data-testid={`affiliate-toggle-${a.code}`}
                        >
                          {a.status === 'active' ? <Pause size={16} /> : <Play size={16} />}
                        </button>
                        <button
                          onClick={() => deleteAffiliate(a.code)}
                          className="p-1.5 rounded hover:bg-red-500/10 text-red-400/70 hover:text-red-400"
                          title="Delete"
                          data-testid={`affiliate-delete-${a.code}`}
                        >
                          <Trash size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Rewards Queue */}
      <div className="mt-6">
        <div className="flex items-center justify-between mb-3">
          <h4 className="heading-font text-base font-medium text-[#cba135]">Reward Queue</h4>
          <div className="flex gap-1">
            {['pending', 'approved', 'rejected', 'paid', 'all'].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`affiliate-filter-${f}`}
                className={`text-xs px-2.5 py-1 rounded ${filter === f ? 'bg-[#cba135] text-[#0e0b04]' : 'bg-[#1a1508] border border-[#cba135]/20 text-[#cba135]/70 hover:text-[#cba135]'}`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-[#1a1508] border border-[#cba135]/15 rounded-lg p-5">
          {rewards.length === 0 ? (
            <p className="text-[#cba135]/60 text-sm" data-testid="reward-empty">No rewards in this state.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[#a8872b] uppercase text-xs tracking-wider">
                  <tr className="border-b border-[#cba135]/10">
                    <th className="text-left py-2 px-2">Code</th>
                    <th className="text-left py-2 px-2">User email</th>
                    <th className="text-left py-2 px-2">Ticket</th>
                    <th className="text-right py-2 px-2">Amount</th>
                    <th className="text-center py-2 px-2">Status</th>
                    <th className="text-left py-2 px-2">Created</th>
                    <th className="text-right py-2 px-2">Actions</th>
                  </tr>
                </thead>
                <tbody className="text-[#cba135]/90">
                  {rewards.map((r) => (
                    <tr key={r.id} className="border-b border-[#cba135]/5" data-testid={`reward-row-${r.id}`}>
                      <td className="py-3 px-2 font-mono font-semibold">{r.code}</td>
                      <td className="py-3 px-2">{r.userEmail || <span className="text-[#cba135]/40">—</span>}</td>
                      <td className="py-3 px-2 font-mono">#{r.ticketNumber || '—'}</td>
                      <td className="py-3 px-2 text-right">{cents(r.amountCents)}</td>
                      <td className="py-3 px-2 text-center">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          r.status === 'paid' ? 'bg-emerald-500/10 text-emerald-300' :
                          r.status === 'approved' ? 'bg-blue-500/10 text-blue-300' :
                          r.status === 'rejected' ? 'bg-red-500/10 text-red-300' :
                          'bg-amber-500/10 text-amber-300'
                        }`}>
                          {r.status}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-[#cba135]/60 text-xs">{r.createdAt ? new Date(r.createdAt).toLocaleString() : '—'}</td>
                      <td className="py-3 px-2 text-right">
                        <div className="flex items-center gap-1 justify-end">
                          {r.status === 'pending' && (
                            <>
                              <button
                                onClick={() => rewardAction(r.id, 'approve')}
                                className="p-1.5 rounded hover:bg-emerald-500/10 text-emerald-400/80 hover:text-emerald-300"
                                title="Approve"
                                data-testid={`reward-approve-${r.id}`}
                              >
                                <CheckCircle size={18} weight="fill" />
                              </button>
                              <button
                                onClick={() => rewardAction(r.id, 'reject')}
                                className="p-1.5 rounded hover:bg-red-500/10 text-red-400/80 hover:text-red-300"
                                title="Reject"
                                data-testid={`reward-reject-${r.id}`}
                              >
                                <XCircle size={18} weight="fill" />
                              </button>
                            </>
                          )}
                          {r.status === 'approved' && (
                            <button
                              onClick={() => {
                                const tx = window.prompt('Enter payout transaction hash (TRC20 USDT or other):');
                                if (tx && tx.trim()) rewardAction(r.id, 'mark-paid', tx.trim());
                              }}
                              className="text-xs px-2 py-1 rounded bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20"
                              data-testid={`reward-mark-paid-${r.id}`}
                            >
                              Mark paid
                            </button>
                          )}
                          {r.payoutTxHash && (
                            <span className="text-xs font-mono text-[#cba135]/50" title={r.payoutTxHash}>
                              {r.payoutTxHash.slice(0, 8)}…
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-[#0e0b04] border border-[#cba135]/20 text-[#cba135]">
          <DialogHeader>
            <DialogTitle className="text-[#cba135]">Create affiliate partner</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-[#cba135]/80 text-xs uppercase tracking-wider">Code *</Label>
              <Input
                data-testid="affiliate-form-code"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, '') })}
                placeholder="PARTNER01"
                className="bg-[#1a1508] border-[#cba135]/20 text-[#cba135] font-mono mt-1"
              />
              <p className="text-xs text-[#cba135]/50 mt-1">A-Z, 0-9, _ -. 3-64 chars. Spaces and other characters are removed automatically. Becomes part of the referral URL.</p>
            </div>
            <div>
              <Label className="text-[#cba135]/80 text-xs uppercase tracking-wider">Partner name</Label>
              <Input
                data-testid="affiliate-form-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="bg-[#1a1508] border-[#cba135]/20 text-[#cba135] mt-1"
              />
            </div>
            <div>
              <Label className="text-[#cba135]/80 text-xs uppercase tracking-wider">Partner email</Label>
              <Input
                data-testid="affiliate-form-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="bg-[#1a1508] border-[#cba135]/20 text-[#cba135] mt-1"
              />
              <p className="text-xs text-[#cba135]/50 mt-1">If provided, a partner login account is auto-created and login credentials are emailed to this address. Also used for self-referral blocking.</p>
            </div>
            <div>
              <Label className="text-[#cba135]/80 text-xs uppercase tracking-wider">Reward structure (combine any)</Label>
              <p className="text-[10px] text-[#cba135]/50 mt-1 mb-2">All 4 reward types can be combined. Set 0 to disable.</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] text-[#cba135]/70 uppercase tracking-wider">CPA / first ticket (€)</Label>
                  <Input
                    data-testid="affiliate-form-cpa-ticket"
                    type="number"
                    min="0"
                    step="0.01"
                    value={(form.cpaTicketCents / 100).toFixed(2)}
                    onChange={(e) => setForm({ ...form, cpaTicketCents: Math.round(parseFloat(e.target.value || '0') * 100) })}
                    className="bg-[#1a1508] border-[#cba135]/20 text-[#cba135] mt-0.5 font-mono text-sm"
                  />
                </div>
                <div>
                  <Label className="text-[10px] text-[#cba135]/70 uppercase tracking-wider">CPA / registration (€)</Label>
                  <Input
                    data-testid="affiliate-form-cpa-reg"
                    type="number"
                    min="0"
                    step="0.01"
                    value={(form.cpaRegistrationCents / 100).toFixed(2)}
                    onChange={(e) => setForm({ ...form, cpaRegistrationCents: Math.round(parseFloat(e.target.value || '0') * 100) })}
                    className="bg-[#1a1508] border-[#cba135]/20 text-[#cba135] mt-0.5 font-mono text-sm"
                  />
                </div>
                <div>
                  <Label className="text-[10px] text-[#cba135]/70 uppercase tracking-wider">CPC / unique click (€)</Label>
                  <Input
                    data-testid="affiliate-form-cpc"
                    type="number"
                    min="0"
                    max="5"
                    step="0.01"
                    value={(form.cpcCents / 100).toFixed(2)}
                    onChange={(e) => setForm({ ...form, cpcCents: Math.round(parseFloat(e.target.value || '0') * 100) })}
                    className={`bg-[#1a1508] mt-0.5 font-mono text-sm ${form.cpcCents > 500 ? 'border-red-500 text-red-400' : 'border-[#cba135]/20 text-[#cba135]'}`}
                  />
                  <p className={`text-[10px] mt-0.5 font-semibold ${form.cpcCents > 500 ? 'text-red-400' : 'text-[#cba135]/60'}`}>
                    Max €5.00 (anti-fraud) · IP-dedup 24h
                  </p>
                </div>
                <div>
                  <Label className="text-[10px] text-[#cba135]/70 uppercase tracking-wider">Revshare (%)</Label>
                  <Input
                    data-testid="affiliate-form-revshare"
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={form.revsharePercent}
                    onChange={(e) => setForm({ ...form, revsharePercent: parseFloat(e.target.value || '0') })}
                    className="bg-[#1a1508] border-[#cba135]/20 text-[#cba135] mt-0.5 font-mono text-sm"
                  />
                  <p className="text-[9px] text-[#cba135]/40 mt-0.5">% of every paid ticket</p>
                </div>
              </div>
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setShowCreate(false)} className="border-[#cba135]/20 text-[#cba135]">Cancel</Button>
              <Button
                data-testid="affiliate-form-submit"
                onClick={createAffiliate}
                className="bg-[#cba135] hover:bg-[#b8902f] text-[#0e0b04] font-medium"
              >
                Create
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AffiliateDetailDialog
        open={!!detailCode}
        onOpenChange={(o) => { if (!o) setDetailCode(null); }}
        code={detailCode}
        token={token}
        apiBase={API}
      />

      <EditAffiliateDialog
        target={editTarget}
        onClose={() => setEditTarget(null)}
        onSave={saveEdit}
      />
    </motion.div>
  );
}

function EditAffiliateDialog({ target, onClose, onSave }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [cpaTicket, setCpaTicket] = useState('20.00');
  const [cpaReg, setCpaReg] = useState('0.00');
  const [cpc, setCpc] = useState('0.00');
  const [revshare, setRevshare] = useState('0');
  const [status, setStatus] = useState('active');

  useEffect(() => {
    if (target) {
      setName(target.name || '');
      setEmail(target.email || '');
      // Prefer modern cpaTicketCents, fall back to legacy cpaAmountCents
      const ticketCents = target.cpaTicketCents ?? target.cpaAmountCents ?? 0;
      setCpaTicket((ticketCents / 100).toFixed(2));
      setCpaReg(((target.cpaRegistrationCents || 0) / 100).toFixed(2));
      setCpc(((target.cpcCents || 0) / 100).toFixed(2));
      setRevshare(String(target.revsharePercent || 0));
      setStatus(target.status || 'active');
    }
  }, [target]);

  const parseEur = (s, max) => {
    const v = parseFloat(String(s).replace(',', '.'));
    if (isNaN(v) || v < 0 || v > max) return NaN;
    return Math.round(v * 100);
  };

  const handleSubmit = () => {
    const tCents = parseEur(cpaTicket, 10000);
    const rCents = parseEur(cpaReg, 10000);
    const cCents = parseEur(cpc, 5);
    const revPct = parseFloat(String(revshare).replace(',', '.'));
    if (isNaN(tCents)) return toast.error('CPA / first ticket must be 0–10000');
    if (isNaN(rCents)) return toast.error('CPA / registration must be 0–10000');
    if (isNaN(cCents)) return toast.error('CPC must be 0–5');
    if (isNaN(revPct) || revPct < 0 || revPct > 100) return toast.error('Revshare must be 0–100');
    onSave({
      name: name.trim() || null,
      email: email.trim() || null,
      cpaTicketCents: tCents,
      cpaRegistrationCents: rCents,
      cpcCents: cCents,
      revsharePercent: revPct,
      status,
    });
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#0e0b04] border border-[#cba135]/20 text-[#cba135] max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-[#cba135]">Edit affiliate {target?.code}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <Label className="text-[#cba135]/80 text-xs uppercase tracking-wider">Partner name</Label>
            <Input
              data-testid="edit-affiliate-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-[#1a1508] border-[#cba135]/20 text-[#cba135] mt-1"
            />
          </div>
          <div>
            <Label className="text-[#cba135]/80 text-xs uppercase tracking-wider">Partner email</Label>
            <Input
              data-testid="edit-affiliate-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-[#1a1508] border-[#cba135]/20 text-[#cba135] mt-1"
            />
            <p className="text-xs text-[#cba135]/50 mt-1">Used for self-referral blocking + welcome / payout emails + partner login.</p>
          </div>
          <div>
            <Label className="text-[#cba135]/80 text-xs uppercase tracking-wider">Reward structure (combine any)</Label>
            <p className="text-[10px] text-[#cba135]/50 mt-1 mb-2">Set 0 to disable. Email shows only active rewards.</p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] text-[#cba135]/70 uppercase tracking-wider">CPA / first ticket (€)</Label>
                <Input data-testid="edit-affiliate-cpa-ticket" type="text" inputMode="decimal" value={cpaTicket} onChange={(e) => setCpaTicket(e.target.value)} className="bg-[#1a1508] border-[#cba135]/20 text-[#cba135] mt-0.5 font-mono text-sm" />
              </div>
              <div>
                <Label className="text-[10px] text-[#cba135]/70 uppercase tracking-wider">CPA / registration (€)</Label>
                <Input data-testid="edit-affiliate-cpa-reg" type="text" inputMode="decimal" value={cpaReg} onChange={(e) => setCpaReg(e.target.value)} className="bg-[#1a1508] border-[#cba135]/20 text-[#cba135] mt-0.5 font-mono text-sm" />
              </div>
              <div>
                <Label className="text-[10px] text-[#cba135]/70 uppercase tracking-wider">CPC / unique click (€)</Label>
                <Input data-testid="edit-affiliate-cpc" type="text" inputMode="decimal" value={cpc} onChange={(e) => setCpc(e.target.value)} className="bg-[#1a1508] border-[#cba135]/20 text-[#cba135] mt-0.5 font-mono text-sm" />
                <p className="text-[9px] text-[#cba135]/40 mt-0.5">Capped at €5 · IP-dedup 24h</p>
              </div>
              <div>
                <Label className="text-[10px] text-[#cba135]/70 uppercase tracking-wider">Revshare (%)</Label>
                <Input data-testid="edit-affiliate-revshare" type="text" inputMode="decimal" value={revshare} onChange={(e) => setRevshare(e.target.value)} className="bg-[#1a1508] border-[#cba135]/20 text-[#cba135] mt-0.5 font-mono text-sm" />
                <p className="text-[9px] text-[#cba135]/40 mt-0.5">% of every paid ticket</p>
              </div>
            </div>
          </div>
          <div>
            <Label className="text-[#cba135]/80 text-xs uppercase tracking-wider">Status</Label>
            <select
              data-testid="edit-affiliate-status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full bg-[#1a1508] border border-[#cba135]/20 text-[#cba135] mt-1 rounded px-3 py-2 text-sm"
            >
              <option value="active">active</option>
              <option value="paused">paused</option>
              <option value="archived">archived</option>
            </select>
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <Button variant="outline" onClick={onClose} className="border-[#cba135]/20 text-[#cba135]">Cancel</Button>
            <Button
              data-testid="edit-affiliate-save"
              onClick={handleSubmit}
              className="bg-[#cba135] hover:bg-[#b8902f] text-[#0e0b04] font-medium"
            >
              Save changes
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
